import { useCallback } from 'react';
import { useImpersonationState } from '@/hooks/useImpersonation';
import { useToast } from '@/hooks/use-toast';

/**
 * Tiny guard hook for high-stakes mutation handlers. Pair with
 * `useImpersonationState` to:
 *   - Disable the button visually when an emulation is active
 *   - Refuse the handler call (with a toast) if the button is clicked anyway
 *
 * Pattern in a handler:
 *
 *   const { isEmulating, blockIfEmulating } = useEmulationGuard();
 *   const handleSend = () => {
 *     if (blockIfEmulating('Sending email')) return;
 *     // …actual mutation
 *   };
 *
 * The disabled state on a button is the primary UX. blockIfEmulating is a
 * belt-and-braces refusal that catches keyboard activation, programmatic
 * dispatch, etc.
 */
export function useEmulationGuard() {
  const state = useImpersonationState();
  const { toast } = useToast();

  const isEmulating = state.active;
  const readOnly = state.active ? state.read_only : false;

  const blockIfEmulating = useCallback(
    (label: string): boolean => {
      if (!isEmulating) return false;
      toast({
        title: 'Blocked while emulating',
        description: `${label} is disabled during emulation as a safety measure.`,
        variant: 'destructive',
      });
      return true;
    },
    [isEmulating, toast],
  );

  return { isEmulating, readOnly, blockIfEmulating };
}
